import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { TRButton } from "@/components/TRButton";
import { useCart } from "@/context/CartContext";
import {
    getCategory,
    PRICE,
    boxImage,
    packagingImage,
    modelImage,
    buildBreadcrumb,
} from "@/lib/catalog";

export default function Configurator() {
    const { category, gender } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { addItem } = useCart();

    const c = getCategory(category);
    const g = c.genders.includes(gender) ? gender : c.genders[0];
    const isBundle = !!c.isBundle;

    const initialBox = params.get("box") === "starter" ? "starter" : "one";
    const initialStyle = params.get("style") || c.styles[1]?.id || c.styles[0]?.id || "";
    const initialColor = params.get("color") === "white" ? "white" : "black";

    const [boxType, setBoxType] = useState(initialBox);
    const [style, setStyle] = useState(initialStyle);
    const [color, setColor] = useState(initialColor);
    const [size, setSize] = useState("");
    const [apparelSize, setApparelSize] = useState("");
    const [qty, setQty] = useState(1);

    const isStarter = boxType === "starter";
    const hideSize = c.assortmentBySize && isStarter;

    const effectiveSize = isBundle
        ? size && apparelSize
            ? `Socks ${size} · Apparel ${apparelSize}`
            : ""
        : hideSize
          ? "S–XXL"
          : size;

    const previewImg = useMemo(() => packagingImage(c.id, color, g), [c.id, color, g]);
    const detailImg = useMemo(
        () => modelImage(c.id, g, isStarter ? c.styles[0]?.id : style, color),
        [c, g, style, color, isStarter],
    );

    const cfg = {
        category: c.id,
        gender: g,
        style: isBundle ? "bundle" : isStarter ? "assorted" : style,
        color,
        size: effectiveSize,
        boxType,
    };
    const breadcrumb = buildBreadcrumb(c.id, cfg);
    const genderSizes = c.sizes[g] || c.sizes.men;
    const trust = [
        `${c.unitsPerBox} premium ${c.pieceNoun}`,
        "Premium rigid box",
        "Free shipping",
        "30-day returns",
        "Secure payment",
    ];

    const handleAdd = () => {
        if (isBundle && (!size || !apparelSize)) {
            toast.error("Please select both sizes.");
            return;
        }
        if (!isBundle && !hideSize && !size) {
            toast.error("Please select a size.");
            return;
        }
        addItem({ ...cfg, quantity: qty });
        toast.success("Added to cart", { description: breadcrumb });
        navigate("/cart");
    };

    return (
        <div data-testid="configurator" className="pt-24 md:pt-28">
            <div className="sticky top-16 md:top-20 z-30 bg-paper/85 backdrop-blur-xl border-y border-line">
                <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-3">
                    <p
                        className="text-[0.72rem] md:text-sm tracking-[0.08em] text-ink/70 truncate"
                        data-testid="config-breadcrumb"
                    >
                        {breadcrumb}
                    </p>
                </div>
            </div>

            <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-10 md:py-16 grid lg:grid-cols-2 gap-12 lg:gap-20">
                {/* Visual */}
                <div className="lg:sticky lg:top-40 lg:self-start">
                    <div className="relative border border-line bg-secondary aspect-square overflow-hidden">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={previewImg}
                                src={previewImg}
                                alt="Selected box"
                                initial={{ opacity: 0, scale: 1.02 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                className="h-full w-full object-cover"
                                data-testid="config-box-image"
                            />
                        </AnimatePresence>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-4">
                        {isBundle ? (
                            <div className="col-span-2 border border-line p-5">
                                <span className="eyebrow text-ink/40">Inside the box</span>
                                <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink/70">
                                    {c.contents.map((it) => (
                                        <li key={it}>· {it}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : c.hasModelPhotos ? (
                            <>
                                <div className="border border-line bg-secondary aspect-[4/3] overflow-hidden">
                                    <img loading="lazy" decoding="async" src={detailImg} alt="Detail" className="h-full w-full object-cover" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <span className="eyebrow text-ink/40">The Fit</span>
                                    <p className="mt-2 text-sm text-ink/65 leading-relaxed">
                                        {isStarter
                                            ? `An assortment across all ${c.styles.length} fits.`
                                            : c.styleCopy[g][style]}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="border border-line bg-ink text-paper aspect-[4/3] flex items-center justify-center">
                                    <span className="font-serif text-2xl">TR</span>
                                </div>
                                <div className="flex flex-col justify-center">
                                    <span className="eyebrow text-ink/40">The Fit</span>
                                    <p className="mt-2 text-sm text-ink/65 leading-relaxed">
                                        {isStarter
                                            ? `An assortment across all ${c.styles.length} fits.`
                                            : c.styleCopy[g][style]}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Options */}
                <div>
                    <span className="eyebrow text-ink/50">
                        {c.label} · {g === "men" ? "Men" : "Women"}
                    </span>
                    <h1 className="mt-4 font-serif font-light text-4xl md:text-5xl tracking-[-0.04em]">
                        {isBundle ? "Build your Essential Box" : "Build your box"}
                    </h1>

                    {/* Box type */}
                    {!isBundle && (
                        <Group label="Box Type" testid="group-box">
                            <div className="grid sm:grid-cols-2 gap-4">
                                {c.boxTypes.map((b) => (
                                    <button
                                        key={b.id}
                                        onClick={() => setBoxType(b.id)}
                                        data-testid={`box-${b.id}`}
                                        className={`text-left p-5 border transition-all duration-300 ${
                                            boxType === b.id
                                                ? "border-ink bg-ink text-paper"
                                                : "border-line hover:border-ink"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-serif text-xl">{b.name}</span>
                                            <span className="text-sm">€{PRICE}</span>
                                        </div>
                                        <p className={`mt-2 text-xs leading-relaxed ${boxType === b.id ? "text-paper/70" : "text-ink/55"}`}>
                                            {b.short}
                                        </p>
                                    </button>
                                ))}
                            </div>
                            {isStarter && (
                                <p className="mt-3 text-xs text-ink/50 italic">
                                    In this box the model no longer applies — it contains all {c.styles.length} fits.
                                </p>
                            )}
                        </Group>
                    )}

                    {/* Model */}
                    <AnimatePresence initial={false}>
                        {!isStarter && !isBundle && c.styles.length > 1 && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                className="overflow-hidden"
                            >
                                <Group label="Model" testid="group-style">
                                    <div className="flex flex-wrap gap-3">
                                        {c.styles.map((s) => (
                                            <Chip key={s.id} active={style === s.id} onClick={() => setStyle(s.id)} testid={`style-${s.id}`}>
                                                {s.name}
                                            </Chip>
                                        ))}
                                    </div>
                                </Group>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Color */}
                    <Group label="Color" testid="group-color">
                        <div className="flex gap-4">
                            {c.colors.map((col) => (
                                <button
                                    key={col.id}
                                    onClick={() => setColor(col.id)}
                                    data-testid={`color-${col.id}`}
                                    className={`flex items-center gap-3 pl-2 pr-5 py-2 border transition-all ${
                                        color === col.id ? "border-ink" : "border-line hover:border-ink/50"
                                    }`}
                                >
                                    <span
                                        className="w-9 h-9 border border-line"
                                        style={{ background: col.id === "black" ? "#131211" : "#ffffff" }}
                                    />
                                    <span className="text-sm">{col.name}</span>
                                    {color === col.id && <Check size={14} strokeWidth={2} />}
                                </button>
                            ))}
                        </div>
                    </Group>

                    {/* Sizes */}
                    {isBundle ? (
                        <>
                            <Group label="Sock Size" testid="group-size">
                                <div className="flex flex-wrap gap-3">
                                    {genderSizes.map((s) => (
                                        <Chip key={s} active={size === s} onClick={() => setSize(s)} testid={`size-${s.replace(/[^0-9a-zA-Z]/g, "")}`}>
                                            {s}
                                        </Chip>
                                    ))}
                                </div>
                            </Group>
                            <Group label="Apparel Size (boxers & undershirts)" testid="group-apparel">
                                <div className="flex flex-wrap gap-3">
                                    {c.apparelSizes.map((s) => (
                                        <Chip key={s} active={apparelSize === s} onClick={() => setApparelSize(s)} testid={`apparel-${s}`}>
                                            {s}
                                        </Chip>
                                    ))}
                                </div>
                            </Group>
                        </>
                    ) : hideSize ? (
                        <Group label="Size" testid="group-size">
                            <p className="text-sm text-ink/60" data-testid="assorted-sizes-note">
                                Assorted across all sizes — S, M, L, XL, XXL.
                            </p>
                        </Group>
                    ) : (
                        <Group label="Size" testid="group-size">
                            <div className="flex flex-wrap gap-3">
                                {genderSizes.map((s) => (
                                    <Chip key={s} active={size === s} onClick={() => setSize(s)} testid={`size-${s.replace(/[^0-9a-zA-Z]/g, "")}`}>
                                        {s}
                                    </Chip>
                                ))}
                            </div>
                        </Group>
                    )}

                    {/* Quantity */}
                    <Group label="Quantity (boxes)" testid="group-qty">
                        <div className="inline-flex items-center border border-line">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="p-4 hover:bg-ink hover:text-paper transition-colors"
                                data-testid="qty-minus"
                                aria-label="Decrease"
                            >
                                <Minus size={14} />
                            </button>
                            <span className="w-14 text-center tabular-nums" data-testid="qty-value">
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => Math.min(20, q + 1))}
                                className="p-4 hover:bg-ink hover:text-paper transition-colors"
                                data-testid="qty-plus"
                                aria-label="Increase"
                            >
                                <Plus size={14} />
                            </button>
                        </div>
                    </Group>

                    <div className="mt-10">
                        <TRButton full onClick={handleAdd} data-testid="add-to-cart">
                            Add to Cart — €{PRICE * qty}
                        </TRButton>
                        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[0.7rem] uppercase tracking-[0.14em] text-ink/45">
                            {trust.map((t) => (
                                <li key={t} className="flex items-center gap-1.5">
                                    <span className="w-1 h-1 bg-ink/40" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Group = ({ label, children, testid }) => (
    <div className="mt-10" data-testid={testid}>
        <p className="eyebrow text-ink/50 mb-4">{label}</p>
        {children}
    </div>
);

const Chip = ({ active, onClick, children, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`px-6 py-3 text-sm border transition-all duration-300 ${
            active ? "border-ink bg-ink text-paper" : "border-line hover:border-ink"
        }`}
    >
        {children}
    </button>
);
